import { Box, Tab, Tabs, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { Route, Routes, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";
import EmissionsConfigurationsList from "./EmissionsConfigurationsList";
import EmissionBodyForm from "./EmissionBodyForm";
import EmissionRecordsTab from "./EmissionRecordsTab";
import EndpointForm from "./EndpointForm";
import NewConfigurationDialog from "./NewConfigurationDialog";
import { useEmissionsConfigurations } from "./useEmissionsConfigurations";

const TAB_PARAM = "tab";
const RECORDS_TAB = "records";

const EmissionsOverview: React.FC = observer(() => {
    const authStore = useStore(AuthStore);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { service, endpoints, bodies, isLoading, hasError, reload } =
        useEmissionsConfigurations();
    const canEdit = authStore.canUserEdit("Emissions Configuration");
    const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);

    // Matches how the backend gates the records endpoints: ultra users bypass the permission
    // check, everyone else needs it. Keeping the ultra branch means records stay reachable even
    // if the permission has not been granted anywhere yet.
    const canViewRecords =
        authStore.isUserUltra() ||
        authStore.canUserView("Emissions Configuration");
    const activeTab =
        canViewRecords && searchParams.get(TAB_PARAM) === RECORDS_TAB
            ? RECORDS_TAB
            : "configurations";

    const onTabChange = (_event: React.SyntheticEvent, tab: string) => {
        const next = new URLSearchParams();
        if (tab === RECORDS_TAB) next.set(TAB_PARAM, RECORDS_TAB);
        setSearchParams(next);
    };

    return (
        <Box
            sx={{
                padding: 4,
                maxWidth: `calc(100% - ${theme.spacing(4)})`,
                width: "1200px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <OrganizationBreadcrumbs pageTitle="Emissions" />
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: "24px",
                        fontWeight: 700,
                        color: theme.palette.gray[700],
                    }}
                >
                    Emissions
                </Typography>
                {canEdit && activeTab === "configurations" && (
                    <AcxButton
                        variant="contained"
                        color="primary"
                        onClick={() => setIsNewDialogOpen(true)}
                    >
                        + New Configuration
                    </AcxButton>
                )}
            </Box>
            <Tabs
                value={activeTab}
                onChange={onTabChange}
                sx={{ borderBottom: 1, borderColor: "divider" }}
            >
                <Tab value="configurations" label="Configurations" />
                {canViewRecords && (
                    <Tab value={RECORDS_TAB} label="Emission Records" />
                )}
            </Tabs>
            {activeTab === "configurations" && (
                <EmissionsConfigurationsList
                    endpoints={endpoints}
                    bodies={bodies}
                    isLoading={isLoading}
                    hasError={hasError}
                    onRetry={reload}
                />
            )}
            {activeTab === RECORDS_TAB && (
                <EmissionRecordsTab
                    service={service}
                    endpoints={endpoints}
                    bodies={bodies}
                />
            )}
            <NewConfigurationDialog
                isOpen={isNewDialogOpen}
                canCreateBody={endpoints.some((endpoint) => endpoint.isActive)}
                onClose={() => setIsNewDialogOpen(false)}
                onContinue={(type) =>
                    navigate(
                        type === "endpoint" ? "endpoints/new" : "bodies/new",
                    )
                }
            />
        </Box>
    );
});

const OrganizationEmissions: React.FC = () => (
    <Routes>
        <Route index element={<EmissionsOverview />} />
        <Route path="endpoints/new" element={<EndpointForm />} />
        <Route path="endpoints/:endpointId" element={<EndpointForm />} />
        <Route path="bodies/new" element={<EmissionBodyForm />} />
        <Route path="bodies/:bodyId" element={<EmissionBodyForm />} />
    </Routes>
);

export default OrganizationEmissions;
